import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { axioKit, fullName } from "../../../../services/utilities";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBProgress,
  MDBBtn,
  MDBTable,
  MDBIcon,
} from "mdbreact";
import {
  Sales,
  OutSource,
  InSource,
  Utilities,
  Stock,
  Physicians,
} from "./wigets";
import Transactions from "./transactions";
import { OutSources, InSources } from "./sources";
import { Expenses, Purchases } from "./calendars";
import { BROWSE as BROWSE_CLINICS } from "../../../../services/redux/slices/diagnostics/clinic/clinicInfo";
import {
  BROWSE as BROWSE_INVENTORY,
  buildInventoryCacheKey,
} from "../../../../services/redux/slices/diagnostics/clinic/clinicInventory";
import { formatScheduleLabel } from "../../../../services/redux/slices/diagnostics/clinic/appointments";
import { HydrateClinicalContext } from "../../../../services/redux/slices/assets/persons/auth";

const formatScheduleTime = ({ hour, min } = {}) => {
  if (typeof hour !== "number") return "";

  const safeMinute = typeof min === "number" ? min : 0;
  const suffix = hour >= 12 ? "PM" : "AM";
  const normalizedHour = hour % 12 || 12;

  return `${String(normalizedHour).padStart(2, "0")}:${String(
    safeMinute,
  ).padStart(2, "0")} ${suffix}`;
};

const getScheduleLabel = (schedule = {}) => {
  const days = Array.isArray(schedule?.days) ? schedule.days : [];
  const timeStart = formatScheduleTime(schedule?.start);
  const timeEnd = formatScheduleTime(schedule?.end);

  if (days.length && timeStart && timeEnd) {
    return `${days.join(", ")} | ${timeStart} - ${timeEnd}`;
  }

  return formatScheduleLabel(schedule?.code || "") || "No schedule details";
};

const getLocationLabel = (location = {}) =>
  [
    location?.room ? `Room ${location.room}` : "",
    location?.floor ? `Floor ${location.floor}` : "",
    location?.building,
    location?.address,
  ]
    .filter(Boolean)
    .join(", ") || "No location set";

const getPhysicianName = (physician = {}) => {
  const directName = fullName(physician?.fullName);
  if (directName && directName !== "-") return directName;

  const userName = fullName(physician?.user?.fullName);
  if (userName && userName !== "-") return userName;

  const ghostName = fullName(physician?.ghost?.fullName || physician?.ghost);
  if (ghostName && ghostName !== "-") return ghostName;

  const fallback = [
    physician?.user?.fullName?.fname || physician?.fullName?.fname,
    physician?.ghost?.fullName?.fname || physician?.ghost?.fname,
    physician?.user?.fullName?.mname || physician?.fullName?.mname,
    physician?.ghost?.fullName?.mname || physician?.ghost?.mname,
    physician?.user?.fullName?.lname || physician?.fullName?.lname,
    physician?.ghost?.fullName?.lname || physician?.ghost?.lname,
    physician?.user?.fullName?.suffix || physician?.fullName?.suffix,
    physician?.ghost?.fullName?.suffix || physician?.ghost?.suffix,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fallback || "Unnamed physician";
};

export default function Dashboard() {
  const [currentMonthSales, setCurrentMonthSales] = useState(0);
  const [lastMonthSales, setLastMonthSales] = useState(0);
  const [currentVouchers, setCurrentVouchers] = useState(0);
  const [lastMonthVouchers, setLastMonthVouchers] = useState(0);
  const [currentMonthOutsources, setCurrentMonthOutsources] = useState(0);
  const [lastMonthOutsources, setLastMonthOutsources] = useState(0);

  const { activePlatform, auth, token } = useSelector(({ auth }) => auth);
  const { collections: clinicCollections = [] } = useSelector(
    ({ clinicInfo }) => clinicInfo,
  );
  const {
    collections: inventoryCollections = [],
    summary: inventorySummary,
    lastFetchedAtByClinic = {},
    isLoading: isInventoryLoading,
  } = useSelector(({ clinicInventory }) => clinicInventory);
  const dispatch = useDispatch();

  const secretaryClinics = useMemo(
    () =>
      (clinicCollections || []).filter((clinic) =>
        (clinic?.secretaries || []).some(
          (secretary) =>
            String(secretary?._id || secretary) === String(auth?._id || ""),
        ),
      ),
    [clinicCollections, auth?._id],
  );

  const secretaryPhysicians = useMemo(
    () =>
      secretaryClinics
        .map((clinic) => {
          const physician = clinic?.physician || clinic?.physicianId || {};
          const physicianId =
            physician?._id ||
            clinic?.physician?._id ||
            clinic?.physicianId?._id;

          if (!physicianId) return null;

          return {
            ...physician,
            clinic,
            clinicId: clinic?._id || "",
            fullName: physician?.fullName || physician?.user?.fullName || {},
          };
        })
        .filter(Boolean),
    [secretaryClinics],
  );

  const physicianClinicCards = useMemo(
    () =>
      secretaryPhysicians.map((physician, index) => {
        const clinic = physician?.clinic || {};
        const clinicSchedules = Array.isArray(clinic?.schedules)
          ? clinic.schedules
          : [];
        const scheduleDetails = clinicSchedules.map((schedule) => ({
          label: getScheduleLabel(schedule),
          type: schedule?.type || "onsite",
          location: getLocationLabel(schedule?.location),
        }));
        const primarySchedule =
          scheduleDetails[0]?.label || "No schedule details";
        const primaryLocation =
          scheduleDetails[0]?.location || "No location set";
        const status = String(clinic?.status || "draft").toLowerCase();

        return {
          id: physician?._id || clinic?._id || index,
          name: getPhysicianName(physician),
          clinicTitle: clinic?.title || "No clinic title",
          specialization:
            clinic?.specialization ||
            clinic?.specializations?.[0] ||
            physician?.specialty ||
            physician?.specialization ||
            "No specialization",
          scheduleCount: scheduleDetails.length,
          scheduleDetails,
          primarySchedule,
          primaryLocation,
          statusLabel: status || "draft",
          statusColor:
            status === "posted"
              ? "success"
              : status === "cancelled"
                ? "danger"
                : status === "done"
                  ? "secondary"
                  : "warning",
        };
      }),
    [secretaryPhysicians],
  );

  const managedClinicIds = useMemo(() => {
    const clinicIds = [
      ...(Array.isArray(activePlatform?.clinics)
        ? activePlatform.clinics.map((clinic) => clinic?._id)
        : []),
      activePlatform?.clinic?._id,
    ]
      .map((id) => String(id || ""))
      .filter(Boolean);

    return Array.from(new Set(clinicIds));
  }, [activePlatform]);

  const inventoryCacheKey = useMemo(
    () => buildInventoryCacheKey(managedClinicIds),
    [managedClinicIds],
  );

  const inventoryLastFetchedAt = lastFetchedAtByClinic[inventoryCacheKey];

  useEffect(() => {
    if (!token || !activePlatform?.branchId) return;

    dispatch(
      BROWSE_CLINICS({
        token,
        params: { branchId: activePlatform.branchId },
      }),
    );
  }, [dispatch, token, activePlatform?.branchId]);

  useEffect(() => {
    if (!secretaryClinics.length && !secretaryPhysicians.length) return;

    const currentPhysicianIds = (activePlatform?.physicians || [])
      .map((physician) =>
        String(physician?._id || physician?.physicianId || ""),
      )
      .filter(Boolean)
      .sort()
      .join(",");
    const nextPhysicianIds = secretaryPhysicians
      .map((physician) => String(physician?._id || ""))
      .filter(Boolean)
      .sort()
      .join(",");
    const currentClinicIds = (activePlatform?.clinics || [])
      .map((clinic) => String(clinic?._id || ""))
      .filter(Boolean)
      .sort()
      .join(",");
    const nextClinicIds = secretaryClinics
      .map((clinic) => String(clinic?._id || ""))
      .filter(Boolean)
      .sort()
      .join(",");

    if (
      currentPhysicianIds === nextPhysicianIds &&
      currentClinicIds === nextClinicIds
    ) {
      return;
    }

    dispatch(
      HydrateClinicalContext({
        physicians: secretaryPhysicians,
        clinics: secretaryClinics,
      }),
    );
  }, [
    dispatch,
    secretaryClinics,
    secretaryPhysicians,
    activePlatform?.clinics,
    activePlatform?.physicians,
  ]);

  useEffect(() => {
    if (!token || !managedClinicIds.length) return;

    const todayKey = new Date().toISOString().slice(0, 10);
    const lastFetchedKey = String(inventoryLastFetchedAt || "").slice(0, 10);

    if (todayKey === lastFetchedKey) return;

    dispatch(
      BROWSE_INVENTORY({
        token,
        key: { clinicIds: managedClinicIds.join(",") },
      }),
    );
  }, [dispatch, token, managedClinicIds, inventoryLastFetchedAt]);

  useEffect(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();

    const queryCurrentMonth = {
      // cashier: auth._id,
      branch: activePlatform.branchId,
      month: month + 1,
      year,
    };

    axioKit
      .universal(
        `finance/bookkeeping/remittances/widgets`,
        token,
        queryCurrentMonth,
      )
      .then((res) => {
        setCurrentMonthSales(res.current.totalSales || 0);
        setLastMonthSales(res.last.totalSales || 0);
        setCurrentVouchers(res.current.totalVouchers || 0);
        setLastMonthVouchers(res.last.totalVouchers || 0);
        console.log(res);
      })
      .catch((err) => console.log(err.message));
    axioKit
      .universal(
        `commerce/pos/services/deals/widgets`,
        token,
        queryCurrentMonth,
      )
      .then((res) => {
        setCurrentMonthOutsources(res.current.totalAmount || 0);
        setLastMonthOutsources(res.last.totalAmount || 0);
        console.log(res);
      })
      .catch((err) => console.log(err.message));
  }, [activePlatform, auth, token]);

  return (
    <MDBContainer fluid id="v6" className="mb-5">
      <section className="mb-4">
        <MDBRow>
          <Sales
            currentMonthSales={currentMonthSales}
            lastMonthSales={lastMonthSales}
          />
          <InSource
            currentVouchers={currentVouchers}
            lastMonthVouchers={lastMonthVouchers}
          />
          <OutSource
            currentMonthOutsources={currentMonthOutsources}
            lastMonthOutsources={lastMonthOutsources}
          />
          <Utilities />
        </MDBRow>
      </section>
      <section className="mb-4">
        <MDBRow>
          <Stock
            collections={inventoryCollections}
            summary={inventorySummary}
            lastFetchedAt={inventoryLastFetchedAt}
            isLoading={isInventoryLoading}
          />
        </MDBRow>
      </section>
      <section className="mb-5">
        <Transactions />
      </section>
      <section className="mb-4">
        <MDBRow>
          <Physicians cards={physicianClinicCards} />
        </MDBRow>
      </section>
      <section>
        <MDBRow>
          <OutSources />
          <InSources />
        </MDBRow>
      </section>
      <section className="mt-2">
        <MDBRow>
          <Expenses />
          <Sales />
          <Purchases />
        </MDBRow>
      </section>
      <section>
        <MDBRow>
          <MDBCol xl="5" md="12">
            <MDBCard className="mb-4">
              <MDBRow>
                <MDBCol md="12" className="text-center">
                  <h5 className="mt-4 mb-4 font-weight-bold">Monthly Sales</h5>
                </MDBCol>
              </MDBRow>
              <MDBCardBody>
                <MDBProgress
                  className="mb-2 mt-1"
                  value={25}
                  barClassName="warning-color"
                />
                <p className="font-small grey-text mb-4">January</p>
                <MDBProgress
                  className="mb-2"
                  value={35}
                  barClassName="red accent-2"
                />
                <p className="font-small grey-text mb-4">Febuary</p>
                <MDBProgress
                  className="mb-2"
                  value={85}
                  barClassName="primary-color"
                />
                <p className="font-small grey-text mb-4">Febuary</p>
                <MDBProgress
                  className="mb-2"
                  value={70}
                  barClassName="light-blue lighten-1"
                />
                <p className="font-small grey-text mb-4">Febuary</p>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol xl="3" md="6" className="mb-2">
            <MDBCard>
              <MDBRow className="mt-4 mb-3">
                <MDBCol md="3" col="3" className="text-left pl-4">
                  <a className="p-2 m-2 fa-lg fb-ic" href="!#">
                    <MDBIcon
                      fab
                      icon="facebook"
                      size="2x"
                      className="blue-text"
                    />
                  </a>
                </MDBCol>
                <MDBCol md="9" col="9" className="text-right pr-5">
                  <p className="font-small grey-text mb-1">Facebook Users</p>
                  <h5 className="ml-4 mb-2 font-weight-bold">4,567 </h5>
                </MDBCol>
              </MDBRow>
            </MDBCard>

            <MDBCard className="mt-4">
              <MDBRow className="mt-4 mb-3">
                <MDBCol md="3" col="3" className="text-left pl-4">
                  <a className="p-2 m-2 fa-lg fb-ic" href="!#">
                    <MDBIcon
                      fab
                      icon="google-plus"
                      size="2x"
                      className="red-text"
                    />
                  </a>
                </MDBCol>
                <MDBCol md="9" col="9" className="text-right pr-5">
                  <p className="font-small grey-text mb-1">Google+ Users</p>
                  <h5 className="ml-4 mb-2 font-weight-bold">2,669 </h5>
                </MDBCol>
              </MDBRow>
            </MDBCard>

            <MDBCard className="mt-4 mb-4">
              <MDBRow className="mt-4 mb-3">
                <MDBCol md="3" col="3" className="text-left pl-4">
                  <a className="p-2 m-2 fa-lg fb-ic" href="!#">
                    <MDBIcon
                      fab
                      icon="facebook"
                      size="2x"
                      className="cyan-text"
                    />
                  </a>
                </MDBCol>
                <MDBCol md="9" col="9" className="text-right pr-5">
                  <p className="font-small grey-text mb-1">Twitter Users</p>
                  <h5 className="ml-4 mb-2 font-weight-bold">3,562 </h5>
                </MDBCol>
              </MDBRow>
            </MDBCard>
          </MDBCol>

          <MDBCol xl="4" md="6" className="mb-2">
            <MDBCard className="mb-4">
              <MDBCardBody>
                <MDBTable responsive>
                  <thead>
                    <tr>
                      <th className="font-weight-bold dark-grey-text">
                        <strong>Month</strong>
                      </th>
                      <th className="font-weight-bold dark-grey-text">
                        <strong>Visits</strong>
                      </th>
                      <th className="font-weight-bold dark-grey-text">
                        <strong>Sales</strong>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>January</td>
                      <td>15</td>
                      <td>307</td>
                    </tr>
                    <tr>
                      <td>Febuary</td>
                      <td>32</td>
                      <td>504</td>
                    </tr>
                    <tr>
                      <td>March</td>
                      <td>41</td>
                      <td>613</td>
                    </tr>
                  </tbody>
                </MDBTable>
                <MDBBtn
                  flat
                  rounded
                  className="grey lighten-3 float-right font-weight-bold dark-grey-text"
                >
                  View full report
                </MDBBtn>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </section>
    </MDBContainer>
  );
}
